import { useState, useEffect, useCallback } from "react";

import { useIsMountedRef } from "@src/hooks/useIsMountedRef";

if (__DEV__) {
  console.warn(
    "SecureStorage: Running on web platform. Using localStorage for development. Do not store sensitive data in production web builds.",
  );
}

/**
 * Web fallback storage using localStorage for persistence during development.
 * In production web builds, consider using a more secure solution or
 * server-side session management.
 */
const webFallbackStorage = {
  get: (key: string): string | undefined => {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage.getItem(`secure_${key}`) ?? undefined;
    }
    return undefined;
  },
  set: (key: string, value: string): void => {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(`secure_${key}`, value);
    }
  },
  delete: (key: string): void => {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(`secure_${key}`);
    }
  },
  clear: (): void => {
    if (typeof window !== "undefined" && window.localStorage) {
      // Only clear our secure_ prefixed keys
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith("secure_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    }
  },
};

export const SecureStorageKeys = {
  jwtToken: "jwtToken",
  userPin: "userPin",
  // Written only by the removed legacy (Django) login. Listed so `clearAll` still wipes them on logout.
  legacyUserId: "legacyUserId",
  legacyUserEmail: "legacyUserEmail",
  legacyAuthToken: "legacyAuthToken",
  legacyUserPin: "legacyUserPin",
} as const;

export type JWT = {
  accessToken: string;
  expiresIn: number;
  issuedAt: number;
  refreshToken: string;
  scope: string;
};

export type SecureStorageKey = (typeof SecureStorageKeys)[keyof typeof SecureStorageKeys];

export interface SecureStorageOptions {
  keychainAccessible?: string;
  requireAuthentication?: boolean;
}

export class SecureStorageError extends Error {
  constructor(
    message: string,
    public readonly operation: "get" | "set" | "remove",
    public readonly key?: string,
    public readonly originalError?: unknown,
  ) {
    super(message);
    this.name = "SecureStorageError";
  }
}

export async function getSecure(key: SecureStorageKey): Promise<string | null> {
  try {
    return webFallbackStorage.get(key) ?? null;
  } catch (error) {
    console.error(`SecureStorageError (get): Failed to read secure key "${key}"`, error);
    throw new SecureStorageError(`Failed to read secure key "${key}"`, "get", key, error);
  }
}

export async function setSecure(key: SecureStorageKey, value: string, options?: SecureStorageOptions): Promise<void> {
  try {
    void options;
    webFallbackStorage.set(key, value);
  } catch (error) {
    console.error(`SecureStorageError (set): Failed to write secure key "${key}"`, error);
    throw new SecureStorageError(`Failed to write secure key "${key}"`, "set", key, error);
  }
}

export async function removeSecure(key: SecureStorageKey): Promise<void> {
  try {
    webFallbackStorage.delete(key);
  } catch (error) {
    console.error(`SecureStorageError (remove): Failed to delete secure key "${key}"`, error);
    throw new SecureStorageError(`Failed to delete secure key "${key}"`, "remove", key, error);
  }
}

export async function hasSecure(key: SecureStorageKey): Promise<boolean> {
  try {
    const value = await getSecure(key);
    return value !== null;
  } catch (error) {
    console.error(`SecureStorageError (has): Failed to check secure key "${key}"`, error);
    return false;
  }
}

export async function clearAllSecure(): Promise<void> {
  try {
    webFallbackStorage.clear();
  } catch (error) {
    console.error("SecureStorageError (clearAll): Failed to clear secure storage", error);
    throw new SecureStorageError("Failed to clear secure storage", "remove", undefined, error);
  }
}

export async function setSecureJSON<T>(key: SecureStorageKey, value: T, options?: SecureStorageOptions): Promise<void> {
  const serialized = JSON.stringify(value);
  await setSecure(key, serialized, options);
}

export async function getSecureJSON<T>(key: SecureStorageKey): Promise<T | null> {
  try {
    const value = await getSecure(key);
    if (value === null) {
      return null;
    }
    return JSON.parse(value) as T;
  } catch (error) {
    console.error(`SecureStorageError (getJSON): Failed to parse JSON for key "${key}"`, error);
    throw new SecureStorageError(`Failed to parse JSON for key "${key}"`, "get", key, error);
  }
}

type SecureStorageListener = (value: string | null) => void;
const listeners = new Map<string, Set<SecureStorageListener>>();

function addListener(key: SecureStorageKey, listener: SecureStorageListener): () => void {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key)!.add(listener);

  return () => {
    const keyListeners = listeners.get(key);
    if (keyListeners) {
      keyListeners.delete(listener);
      if (keyListeners.size === 0) {
        listeners.delete(key);
      }
    }
  };
}

function notifyListeners(key: SecureStorageKey, value: string | null): void {
  const keyListeners = listeners.get(key);
  if (keyListeners) {
    keyListeners.forEach((listener) => {
      listener(value);
    });
  }
}

export function useSecureStorage(
  key: SecureStorageKey,
  options?: SecureStorageOptions,
): [string | null, (value: string | null) => Promise<void>, boolean, Error | null] {
  const [value, setValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useIsMountedRef();

  useEffect(() => {
    const loadValue = async () => {
      try {
        setIsLoading(true);
        const storedValue = await getSecure(key);
        if (isMounted.current) {
          setValue(storedValue);
          setError(null);
        }
      } catch (err) {
        if (isMounted.current) {
          setError(err as Error);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadValue();
  }, [key, isMounted]);

  useEffect(() => {
    const unsubscribe = addListener(key, (newValue) => {
      setValue(newValue);
    });

    return unsubscribe;
  }, [key]);

  const updateValue = useCallback(
    async (newValue: string | null) => {
      try {
        if (newValue === null) {
          await removeSecure(key);
        } else {
          await setSecure(key, newValue, options);
        }
        setValue(newValue);
        notifyListeners(key, newValue);
        setError(null);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [key, options],
  );

  return [value, updateValue, isLoading, error];
}

export function useSecureJSON<T>(
  key: SecureStorageKey,
  options?: SecureStorageOptions,
): [T | null, (value: T | null) => Promise<void>, boolean, Error | null] {
  const [value, setValue, isLoading, error] = useSecureStorage(key, options);

  const parsedValue = value ? (JSON.parse(value) as T) : null;

  const updateValue = useCallback(
    async (newValue: T | null) => {
      if (newValue === null) {
        await setValue(null);
      } else {
        await setValue(JSON.stringify(newValue));
      }
    },
    [setValue],
  );

  return [parsedValue, updateValue, isLoading, error];
}

export const secureStorage = {
  get: getSecure,
  set: setSecure,
  remove: removeSecure,
  has: hasSecure,
  clearAll: clearAllSecure,
  getJSON: getSecureJSON,
  setJSON: setSecureJSON,
};
